'use server'

import { apiRequest, ApiResponse } from '../request'

export type BatchStatus =
    | 'BATCH_ACTIVE'
    | 'BATCH_LOW'
    | 'BATCH_DEPLETED'
    | 'BATCH_EXPIRED'
    | 'BATCH_QUARANTINE'
    | 'BATCH_RECALLED'

export interface Supplier {
    id: number
    name: string
    cnpj?: string
    email?: string
    phone?: string
}

export interface MedicationBatch {
    id: number
    companyId: number
    medicationId: number
    supplierId?: number
    batchNumber: string
    manufacturingDate?: string
    expirationDate: string
    receivedDate: string
    initialQuantity: number
    currentQuantity: number
    unitCost: number
    totalCost: number
    invoiceNumber?: string
    status: BatchStatus
    location?: string
    notes?: string
    createdAt: string
    updatedAt: string
    medication?: {
        id: number
        name: string
        genericName?: string
        unit: string
    }
    supplier?: Supplier
}

export interface CreateBatchData {
    medicationId: number
    supplierId?: number
    batchNumber: string
    manufacturingDate?: string
    expirationDate: string
    receivedDate?: string
    initialQuantity: number
    unitCost: number
    invoiceNumber?: string
    location?: string
    notes?: string
}

export interface UpdateBatchData {
    status?: BatchStatus
    location?: string
    notes?: string
}

export interface AdjustStockData {
    adjustment: number
    reason: string
    notes?: string
}

export interface BatchFilters {
    medicationId?: number
    status?: BatchStatus
    expiringDays?: number
}

// Batch CRUD
export async function getBatches(
    filters?: BatchFilters
): Promise<ApiResponse<MedicationBatch[]>> {
    return apiRequest<MedicationBatch[]>('stock/batches', { params: filters })
}

export async function getBatch(
    id: number
): Promise<ApiResponse<MedicationBatch>> {
    return apiRequest<MedicationBatch>(`stock/batches/${id}`, {})
}

export async function createBatch(
    data: CreateBatchData
): Promise<ApiResponse<MedicationBatch>> {
    return apiRequest<MedicationBatch>('stock/batches', { data }, 'post')
}

export async function updateBatch(
    id: number,
    data: UpdateBatchData
): Promise<ApiResponse<MedicationBatch>> {
    return apiRequest<MedicationBatch>(`stock/batches/${id}`, { data }, 'patch')
}

export async function deleteBatch(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`stock/batches/${id}`, {}, 'delete')
}

// Batch operations
export async function adjustBatchStock(
    id: number,
    data: AdjustStockData
): Promise<ApiResponse<MedicationBatch>> {
    return apiRequest<MedicationBatch>(
        `stock/batches/${id}/adjust`,
        { data },
        'patch'
    )
}

export async function quarantineBatch(
    id: number,
    reason: string
): Promise<ApiResponse<MedicationBatch>> {
    return apiRequest<MedicationBatch>(
        `stock/batches/${id}/quarantine`,
        { data: { reason } },
        'patch'
    )
}

export async function recallBatch(
    id: number,
    reason: string
): Promise<ApiResponse<MedicationBatch>> {
    return apiRequest<MedicationBatch>(
        `stock/batches/${id}/recall`,
        { data: { reason } },
        'patch'
    )
}

export async function reactivateBatch(
    id: number
): Promise<ApiResponse<MedicationBatch>> {
    return apiRequest<MedicationBatch>(
        `stock/batches/${id}/reactivate`,
        {},
        'patch'
    )
}

export async function selectBatchFEFO(
    medicationId: number,
    quantity: number
): Promise<ApiResponse<MedicationBatch | null>> {
    return apiRequest<MedicationBatch | null>(
        `stock/batches/select-fefo/${medicationId}`,
        { params: { quantity } }
    )
}

export async function getExpiringBatches(
    days?: number
): Promise<ApiResponse<MedicationBatch[]>> {
    return apiRequest<MedicationBatch[]>('stock/batches/expiring', {
        params: { days },
    })
}

// Supplier CRUD
export async function getSuppliers(): Promise<ApiResponse<Supplier[]>> {
    return apiRequest<Supplier[]>('stock/batches/suppliers', {})
}

export async function createSupplier(data: {
    name: string
    cnpj?: string
    email?: string
    phone?: string
    address?: string
}): Promise<ApiResponse<Supplier>> {
    return apiRequest<Supplier>('stock/batches/suppliers', { data }, 'post')
}

// Labels
export const BATCH_STATUS_LABELS: Record<BatchStatus, string> = {
    BATCH_ACTIVE: 'Ativo',
    BATCH_LOW: 'Baixo',
    BATCH_DEPLETED: 'Esgotado',
    BATCH_EXPIRED: 'Vencido',
    BATCH_QUARANTINE: 'Quarentena',
    BATCH_RECALLED: 'Recolhido',
}

export const BATCH_STATUS_COLORS: Record<BatchStatus, string> = {
    BATCH_ACTIVE: 'badge-success',
    BATCH_LOW: 'badge-warning',
    BATCH_DEPLETED: 'badge-ghost',
    BATCH_EXPIRED: 'badge-error',
    BATCH_QUARANTINE: 'badge-warning',
    BATCH_RECALLED: 'badge-error',
}
